import React, { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./AuthContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import ErrorBoundary from "./components/ErrorBoundary";
import CookieConsent from "./components/CookieConsent";
import { Toaster } from "sonner";
import { useAutoLogout } from "./hooks/useAutoLogout";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Route type constants to avoid inline array creation
const EMPLOYEE_TYPES = ['employee'];
const COMPANY_TYPES = ['company'];
const ADMIN_TYPES = ['admin'];

// Bouncing SICKLO Letters Loader Component - matches logo font exactly
// Slower bounce animation, smaller font sizes for elegance
export const SickloLoader = ({ size = 'md', showText = false }) => {
  const letters = ['S', 'I', 'C', 'K', 'L', 'O'];
  const sizes = {
    sm: 'text-lg',      // Smaller: was text-2xl
    md: 'text-2xl',     // Smaller: was text-4xl  
    lg: 'text-4xl'      // Smaller: was text-6xl
  };
  const letterSize = sizes[size] || sizes.md;
  
  return (
    <div className="flex flex-col items-center justify-center">
      <div className="flex items-end">
        {letters.map((char, index) => (
          <span
            key={`logo-letter-${char}-${index}`}
            className={`${letterSize} font-logo font-extrabold text-[#FF3333]`}
            style={{
              animation: 'sicklo-bounce 1.8s ease-in-out infinite',  // Slower: 1.8s
              animationDelay: `${index * 0.15}s`  // More staggered
            }}
          >
            {char}
          </span>
        ))}
      </div>
      {showText && (
        <p className="mt-3 text-xs text-neutral-500 animate-pulse font-body">Loading...</p>
      )}
    </div>
  );
};

// Instagram-style Spinner - for inline button/action loading states
// Clean rotating circle, small and subtle
export const SickloDots = ({ size = 14, color = '#FF3333', className = '' }) => {
  return (
    <span
      className={`inline-block ${className}`}
      style={{
        width: size,
        height: size,
        border: `2px solid ${color}33`,
        borderTopColor: color,
        borderRadius: '50%',
        animation: 'sicklo-spin 0.7s linear infinite'
      }}
    />
  );
};

// Loading component for lazy-loaded routes
const PageLoader = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-neutral-50">
    <SickloLoader size="sm" showText />
  </div>
);

// Lazy load pages for better initial bundle size
// Desktop Pages
const Welcome = lazy(() => import("./pages/Welcome"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const RegisterEmployee = lazy(() => import("./pages/RegisterEmployee"));
const RegisterCompany = lazy(() => import("./pages/RegisterCompany"));
const EmployeeDashboard = lazy(() => import("./pages/EmployeeDashboard"));
const CompanyProfile = lazy(() => import("./pages/CompanyProfile"));
const HelpPage = lazy(() => import("./pages/HelpPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
// Admin pages removed - now using SharedEmployeeProfile with isAdminView prop
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const VerifyEmploymentPage = lazy(() => import("./pages/VerifyEmploymentPage"));

// Mobile App Pages
const WelcomeApp = lazy(() => import("./pages/WelcomeApp"));
const CompanyDashboardApp = lazy(() => import("./pages/CompanyDashboardApp"));
const EmployeeProfile = lazy(() => import("./pages/EmployeeProfile"));
const EmployeeRecords = lazy(() => import("./pages/EmployeeRecords"));
const EmployeeAccess = lazy(() => import("./pages/EmployeeAccess"));
const SharedEmployeeProfile = lazy(() => import("./pages/SharedEmployeeProfile"));
const EmployeePreviewProfile = lazy(() => import("./pages/EmployeePreviewProfile"));
const PublicSharedProfile = lazy(() => import("./pages/PublicSharedProfile"));
const TeamMemberSignup = lazy(() => import("./pages/TeamMemberSignup"));

// Check if mobile device
const isMobile = () => {
  return window.innerWidth < 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedTypes }) => {
  const { user, loading } = useAuth();
  
  // Auto-logout after 60 minutes of inactivity
  useAutoLogout(60);

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-neutral-50">
        <SickloLoader size="sm" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedTypes && !allowedTypes.includes(user.user_type)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Public Route (redirects if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-neutral-50">
        <SickloLoader size="sm" />
      </div>
    );
  }

  if (user) {
    if (user.user_type === 'admin') {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (user.user_type === 'employee') {
      // Always redirect employees to their records page
      return <Navigate to="/employee/records" replace />;
    } else {
      return <Navigate to="/company/dashboard" replace />;
    }
  }

  return children;
};

function App() {
  const [mobile, setMobile] = useState(isMobile());

  useEffect(() => {
    const handleResize = () => {
      setMobile(isMobile());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Use app version for mobile, web version for desktop
  const WelcomeComponent = mobile ? WelcomeApp : Welcome;
  // Always use EmployeeDashboard for consistent behavior across all devices (matching CompanyDashboardApp approach)
  const EmployeeDashboardComponent = EmployeeDashboard;
  // Always use CompanyDashboardApp for consistent behavior across all devices
  const CompanyDashboardComponent = CompanyDashboardApp;

  return (
    <ErrorBoundary>
      <AuthProvider>
      <NotificationProvider>
        <div className="App">
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<PageLoader />}>
              <main id="main-content">
              <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              <Route
                path="/register"
                element={
                  <PublicRoute>
                    <Register />
                  </PublicRoute>
                }
              />
              <Route
                path="/register/employee"
                element={<RegisterEmployee />}
              />
              <Route
                path="/register/company"
                element={<RegisterCompany />}
              />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* Team Member Signup (Magic Link) */}
              <Route path="/team-member-signup" element={<TeamMemberSignup />} />

              {/* Magic Link Verification (No Auth Required) */}
              <Route path="/verify-employment/:token" element={<VerifyEmploymentPage />} />

              {/* Help & Contact Pages (Public) */}
              <Route path="/help" element={<HelpPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/cookies" element={<CookiePolicyPage />} />

              {/* Public Share Profile Gate - requires login to view */}
              <Route path="/share/:shareToken" element={<PublicSharedProfile />} />

              {/* Protected Employee Routes */}
              <Route
                path="/employee/dashboard"
                element={
                  <ProtectedRoute allowedTypes={EMPLOYEE_TYPES}>
                    <EmployeeDashboardComponent />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employee/profile"
                element={
                  <ProtectedRoute allowedTypes={EMPLOYEE_TYPES}>
                    <EmployeeProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employee/preview-profile"
                element={
                  <ProtectedRoute allowedTypes={EMPLOYEE_TYPES}>
                    <EmployeePreviewProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employee/records"
                element={
                  <ProtectedRoute allowedTypes={EMPLOYEE_TYPES}>
                    <EmployeeRecords />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employee/access"
                element={
                  <ProtectedRoute allowedTypes={EMPLOYEE_TYPES}>
                    <EmployeeAccess />
                  </ProtectedRoute>
                }
              />
              {/* Protected Company Routes */}
              <Route
                path="/company/dashboard"
                element={
                  <ProtectedRoute allowedTypes={COMPANY_TYPES}>
                    <CompanyDashboardComponent />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/company/profile"
                element={
                  <ProtectedRoute allowedTypes={COMPANY_TYPES}>
                    <CompanyProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/company/shared-profile/:accessId"
                element={
                  <ProtectedRoute allowedTypes={COMPANY_TYPES}>
                    <SharedEmployeeProfile />
                  </ProtectedRoute>
                }
              />

              {/* Protected Admin Routes */}
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute allowedTypes={ADMIN_TYPES}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/employee-profile/:userId"
                element={
                  <ProtectedRoute allowedTypes={ADMIN_TYPES}>
                    <SharedEmployeeProfile isAdminView={true} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/company-profile/:companyId"
                element={
                  <ProtectedRoute allowedTypes={ADMIN_TYPES}>
                    <CompanyDashboardApp isAdminView={true} />
                  </ProtectedRoute>
                }
              />

              {/* Catch all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
              </main>
            </Suspense>
            <CookieConsent />
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </div>
      </NotificationProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
